import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Plus, 
  Search, 
  ClipboardCheck, 
  Package,
  MapPin,
  CheckSquare
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface QuickActionsAssetManagementProps {
  readonly className?: string;
}

export function QuickActionsAssetManagement({ className }: QuickActionsAssetManagementProps): React.ReactElement {
  const navigate = useNavigate();

  const actions = [
    {
      icon: Plus,
      label: 'Add Asset',
      description: 'Register new asset',
      onClick: () => navigate('/assets/new?mode=create'),
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-950 hover:bg-blue-100 dark:hover:bg-blue-900',
    },
    {
      icon: Search,
      label: 'Find Asset',
      description: 'Search & filter',
      onClick: () => navigate('/assets'),
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-950 hover:bg-purple-100 dark:hover:bg-purple-900',
    },
    {
      icon: CheckSquare,
      label: 'Tasks',
      description: 'Manage tasks',
      onClick: () => navigate('/tasks'),
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-950 hover:bg-orange-100 dark:hover:bg-orange-900',
    },
    {
      icon: ClipboardCheck,
      label: 'Active Audits',
      description: 'View audits',
      onClick: () => navigate('/audits/active'),
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-950 hover:bg-green-100 dark:hover:bg-green-900',
    },
    {
      icon: Package,
      label: 'Categories',
      description: 'Manage groups',
      onClick: () => navigate('/administration/categories'),
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-950 hover:bg-amber-100 dark:hover:bg-amber-900',
    },
    {
      icon: MapPin,
      label: 'Locations',
      description: 'View sites',
      onClick: () => navigate('/administration/locations'),
      color: 'text-indigo-600 dark:text-indigo-400',
      bgColor: 'bg-indigo-50 dark:bg-indigo-950 hover:bg-indigo-100 dark:hover:bg-indigo-900',
    },
  ];

  return (
    <Card className={cn('relative overflow-hidden border-x-0 border-t-0 rounded-none shadow-sm bg-white dark:bg-black', className)}>
      <CardContent className="p-4">
        <div className="grid grid-cols-6 gap-3">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <Button
                key={action.label}
                variant="ghost"
                className={cn(
                  'h-auto flex flex-col items-center justify-center gap-2 p-4',
                  action.bgColor
                )}
                onClick={action.onClick}
              >
                <Icon className={cn('h-6 w-6', action.color)} />
                <div className="text-center">
                  <div className="text-sm font-semibold">{action.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{action.description}</div>
                </div>
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
